import * as Sentry from "@sentry/browser";
import { StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import useScrollToTop from "core-integration/src/hooks/useScrollToTop";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getBootstrapped } from "core-integration/src/store/bootstrap/reducers";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import { getServerError } from "core-integration/src/store/global/reducers";
import { IServerError } from "core-integration/src/store/global/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import { initializeGtm } from "shared-utils/src/gtm";
import styled, { ThemeProvider } from "styled-components";
import GlobalStyle from "../GlobalStyle";
import "../fonts.css";
import i18n from "../i18n";
import theme from "../theme";
import { getIsWebView } from "../utils/webView";
import ElementDialog from "./ElementDialog";
import Footer from "./Footer";
import FooterWebview from "./FooterWebview";
import GameHeader from "./GameHeader";
import { Main, Wrapper } from "./Layout";
import Routes from "./Routes";
import ServerError from "./ServerError";
import AutoJoinDialog from "./leagues/AutoJoinDialog";

const LoadingWrapper = styled.div`
  white-space: nowrap;
  margin-left: ${({ theme }) => theme.space[2]};
  margin-right: ${({ theme }) => theme.space[2]};
`;

interface IProps {
  bootstrapped: boolean;
  serverError: IServerError | null;
}

export const Loading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Main>
        <LoadingWrapper>{t("loading", "Loading")} ...</LoadingWrapper>
      </Main>
    </Wrapper>
  );
};

const App: React.FC<IProps> = ({ bootstrapped, serverError }) => {
  const player = useSelector(getPlayerData);
  const userId = player?.id?.toString();

  const { client, isLoading } = useClientAsyncInit(
    "client-ilJMgkCe7KBFuB66i4EkiIUGwo7IW0okgjBllQP8tIH",
    { userID: userId?.toString() },
    {
      environment: {
        tier: import.meta.env.VITE_ENVIRONMENT,
      },
      plugins: [
        new StatsigAutoCapturePlugin(),
        new StatsigSessionReplayPlugin(),
      ],
    },
  );

  useEffect(() => {
    if (!client || !userId) return;
    client.updateUserAsync({ userID: userId.toString() });
  }, [client, userId]);

  // Handle scroll restoration
  useScrollToTop();

  useEffect(() => {
    if (userId) {
      initializeGtm(import.meta.env.VITE_GTM_ID, {
        userId: userId || "",
        loggedIn: userId ? true : false,
      });
    }
  }, [userId]);

  // Loading condition to ensure Statsig is loaded before rendering
  const loading = player ? player && !userId && isLoading : false;

  return (
    <React.StrictMode>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <React.Suspense fallback={<div />}>
          <title>{i18n.t("app.gameName", "RSL Fantasy")}</title>
          <meta
            name="description"
            content={i18n.t<string>(
              "app.description1n",
              "The new RSL Fantasy game for the 2026/27 season.",
            )}
          />
          <div className="ism">
            <GameHeader />
            {serverError ? (
              <ServerError error={serverError} />
            ) : bootstrapped && !loading ? (
              <StatsigProvider client={client}>
                <Routes />
              </StatsigProvider>
            ) : (
              // Show layout without "Loading..." text for smoother transition
              <Routes />
            )}
            {getIsWebView() ? <FooterWebview /> : <Footer />}
            <AutoJoinDialog />
            <ElementDialog />
          </div>
        </React.Suspense>
      </ThemeProvider>
    </React.StrictMode>
  );
};

interface IContainerPropsFromState {
  bootstrapped: boolean;
  serverError: IServerError | null;
}

interface IState {
  eventId: string | null;
  error: Error | null;
}

interface IContainerPropsFromDispatch {
  bootstrapApp: () => void;
}
type ContainerProps = IContainerPropsFromState & IContainerPropsFromDispatch;
class AppContainer extends React.Component<ContainerProps, IState> {
  public state: IState = { error: null, eventId: null };

  public componentDidMount() {
    this.props.bootstrapApp();
  }

  public componentDidCatch(error: Error | null, errorInfo: object) {
    this.setState({ error });
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
      Sentry.withScope((scope) => {
        // Sentry's setExtras expects a record of string keys to any values.
        // errorInfo is an object, but may not have string keys, so we cast it.
        scope.setExtras(errorInfo as Record<string, any>);
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  public render() {
    // JS Error
    if (this.state.error) {
      // Can't reliably use any of our components, eg Button. This should
      //probably be in Norwegian can't use react to do it though ...
      return (
        <>
          <h2>Error</h2>
          <p>We're sorry - something's gone wrong.</p>
          {import.meta.env.VITE_SENTRY_ENVIRONMENT && (
            <>
              <p>
                Our team has been notified, but click here to fill out a report.
              </p>
              <button
                onClick={() =>
                  Sentry.showReportDialog({
                    eventId: this.state.eventId as string,
                  })
                }
              >
                Report feedback
              </button>
            </>
          )}
        </>
      );
    }
    // All OK - Render App
    return (
      <App
        bootstrapped={this.props.bootstrapped}
        serverError={this.props.serverError}
      />
    );
  }
}

const mapStateToProps = (state: RootState): IContainerPropsFromState => ({
  bootstrapped: getBootstrapped(state),
  serverError: getServerError(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
): IContainerPropsFromDispatch => ({
  bootstrapApp: () => dispatch(bootstrap()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
